import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Activity,
  ChevronRight,
  TrendingUp,
  FileText,
  HelpCircle,
  Edit2
} from 'lucide-react';
import { Button } from '../../components/ui';
import { ProfileEditModal } from './ProfileEditModal';
import { SupportRequestModal } from './SupportRequestModal';

export function ProfilePage() {
  const { user, patient, aclCare, updateAclCare, isProvider, isAdmin } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [updatingDataSharing, setUpdatingDataSharing] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = () => {
    const first = user?.first_name?.[0] || '';
    const last = user?.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getCurrentPhaseLabel = () => {
    const phase = aclCare?.current_phase;
    if (!phase || phase === 0) return 'Not Started';
    return `Phase ${phase}`;
  };

  // Organization data sharing
  const hasOrganization = user?.organization_id && user?.organization;
  const orgName = user?.organization?.name || 'your organization';
  const dataSharingAccepted = aclCare?.org_data_sharing_accepted || false;

  const handleDataSharingToggle = async () => {
    if (updatingDataSharing) return;
    
    setUpdatingDataSharing(true);
    try {
      const newValue = !dataSharingAccepted;
      await updateAclCare({ org_data_sharing_accepted: newValue });
    } catch (err) {
      console.error('Error updating data sharing preference:', err);
    } finally {
      setUpdatingDataSharing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Profile Hero Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header Background */}
        <div className="h-24 bg-gradient-to-r from-primary to-primary-600" />
        
        {/* Profile Content */}
        <div className="px-4 pb-6 -mt-12">
          {/* Avatar */}
          <div className="flex justify-between items-end mb-4">
            <div className="h-24 w-24 rounded-full bg-white border-4 border-white shadow-md flex items-center justify-center">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">
                  {getInitials()}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditModal(true)}
              className="mb-2"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>

          {/* Name */}
          <h1 className="text-2xl font-bold text-gray-900">
            {user?.first_name} {user?.last_name}
          </h1>
          
          {/* Current Phase Badge - Only for patients */}
          {!isProvider && !isAdmin && (
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                <Activity className="h-4 w-4 mr-1" />
                {getCurrentPhaseLabel()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
        
        <div className="space-y-4">
          <div className="flex items-center">
            <Mail className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-gray-900">{user?.email || 'Not set'}</p>
            </div>
          </div>

          <div className="flex items-center">
            <Phone className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="text-gray-900">{user?.phone || patient?.phone || 'Not set'}</p>
            </div>
          </div>

          <div className="flex items-center">
            <MapPin className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="text-gray-900">
                {patient?.address?.city && patient?.address?.state
                  ? `${patient.address.city}, ${patient.address.state}`
                  : 'Not set'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Injury Information - Only show for patients */}
      {!isProvider && !isAdmin && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Injury Information</h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <Activity className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Injury Side</p>
                <p className="text-gray-900 capitalize">{aclCare?.injury_side || 'Not set'} Knee</p>
              </div>
            </div>

            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Injury Date</p>
                <p className="text-gray-900">{formatDate(aclCare?.injury_date)}</p>
              </div>
            </div>

            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Surgery Date</p>
                <p className="text-gray-900">{formatDate(aclCare?.surgery_date)}</p>
              </div>
            </div>

            {aclCare?.surgeon_name && (
              <div className="flex items-center">
                <User className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Surgeon</p>
                  <p className="text-gray-900">{aclCare.surgeon_name}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Sharing Settings - Only show for organization users who are patients */}
      {hasOrganization && !isProvider && !isAdmin && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Sharing</h2>
          
          <div className="flex items-start gap-3">
            <div className="flex items-center h-6">
              <button
                type="button"
                role="switch"
                aria-checked={dataSharingAccepted}
                disabled={updatingDataSharing}
                onClick={handleDataSharingToggle}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                  ${dataSharingAccepted ? 'bg-primary' : 'bg-gray-200'}
                  ${updatingDataSharing ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${dataSharingAccepted ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
            <div className="flex-1">
              <span className="text-sm text-gray-900 font-medium block mb-1">
                Data Sharing with {orgName}
                <span className="text-gray-500 font-normal ml-1">(Optional)</span>
              </span>
              <span className="text-sm text-gray-600">
                You were referred by {orgName}. If enabled, {orgName} will be able to view 
                your data and progress. This is optional and you can still use ACL Care without sharing your data.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <h2 className="text-lg font-semibold text-gray-900 p-4 pb-2">Quick Actions</h2>
        
        <div className="divide-y divide-gray-100">
          {/* View Progress - Only for patients */}
          {!isProvider && !isAdmin && (
            <button
              onClick={() => window.location.href = '/profile/progress'}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">View Progress</p>
                  <p className="text-sm text-gray-500">Track your recovery journey</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          )}

          {/* Test Out Report - Only for patients */}
          {!isProvider && !isAdmin && (
            <button
              onClick={() => window.location.href = '/profile/test-out-report'}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Test Out Report</p>
                  <p className="text-sm text-gray-500">View your assessment results</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          )}

          {/* Support Request - For everyone */}
          <button
            onClick={() => setShowSupportModal(true)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                <HelpCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Support Request</p>
                <p className="text-sm text-gray-500">Get help from our team</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <ProfileEditModal onClose={() => setShowEditModal(false)} />
      )}

      {showSupportModal && (
        <SupportRequestModal onClose={() => setShowSupportModal(false)} />
      )}
    </div>
  );
}